import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { endOfDay, startOfDay } from 'date-fns';
import debounce from 'lodash.debounce';
import { useState } from 'react';
import { User } from 'lucide-react';
import {
	RequirementChip,
	ChipList,
	DataGrid,
	FilterAsyncAutoComplete,
	FilterAutoCompleteById,
	FilterDateRange,
	FilterSearch,
	Badge,
	Spinner,
} from '@repo/components';
import { EventForm } from '@repo/forms';
import { EventEmitter, sortToString, stringToSort, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getCompanyEvents } from 'src/api/company';
import { getEventTypes } from 'src/api/admin/types';
import { getAllZones } from 'src/api/admin/zones';
import { getAllTags } from 'src/api/admin/tags';
import { useLocalStorage, useBreadcrumbLevel } from '@repo/components/hooks';
import { Project } from '@repo/shared/domain';

export const Route = createFileRoute('/company_/$groupId/events/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		search: (search.search as string) || '',
		page: (search.page as number) || 0,
		sort: (search.sort as string) || 'startTime:desc',
		typeIds: (search.typeIds as string) || '',
		zoneIds: (search.zoneIds as string) || '',
		userIds: (search.userIds as string) || '',
		projectIds: (search.projectIds as string) || '',
		from: (search.from as string) || '',
		until: (search.until as string) || '',
		tab: (search.tab as string) || '0',
	}),
});

const getColumns = (usesSubEvents: boolean) => [
	{ field: 'name', headerName: 'Name', flex: 1 },
	...(usesSubEvents
		? [
				{
					field: 'project',
					headerName: 'Project',
					flex: 1,
					renderCell: (c: { value: Project | null }) => (c.value?.name ? <Badge>{c.value.name}</Badge> : null),
					sortable: false,
				},
			]
		: []),
	{
		field: 'type',
		headerName: 'Type',
		flex: 1,
		renderCell: (c: any) => <RequirementChip object={c.row} />,
		sortable: false,
	},
	{
		field: 'startTime',
		headerName: 'Date From',
		width: 120,
		valueFormatter: (params: any) => new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'finishTime',
		headerName: 'Date To',
		width: 120,
		valueFormatter: (params: any) => new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'zones',
		headerName: 'Zones',
		flex: 1,
		renderCell: (c: any) => <ChipList data={c.value} getLabel={(o: any) => o?.name} />,
		sortable: false,
	},
	{
		field: 'groups',
		headerName: 'Companies',
		flex: 1,
		renderCell: (c: any) => <ChipList data={c.value} getLabel={(o: any) => o?.name} showAll />,
		sortable: false,
	},
	{
		field: 'users',
		headerName: 'Users',
		flex: 1,
		renderCell: (c: any) => (
			<ChipList
				data={c.value?.map((user: any) => ({ ...user, status: undefined }))}
				getLabel={(o: any) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email)}
				icon={<User size={14} className="text-gray-500" />}
			/>
		),
		sortable: false,
	},
	{
		field: 'admins',
		headerName: 'Contacts',
		flex: 1,
		renderCell: (c: any) => (
			<ChipList
				data={c.value?.map((user: any) => ({ ...user, status: undefined }))}
				getLabel={(o: any) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email)}
				icon={<User size={14} className="text-gray-500" />}
			/>
		),
		sortable: false,
	},
];

function RouteComponent() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const updateQueryParams = useUpdateQueryParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();
	const { updateValue } = useLocalStorage<string>('companyPortalEventsBackUrl');
	const [isDialogOpen, setDialogOpen] = useState(false);

	const query = constructQuery({
		page: searchParams.page,
		sort: searchParams.sort,
		search: searchParams.search || undefined,
		typeIds: searchParams.typeIds || undefined,
		zoneIds: searchParams.zoneIds || undefined,
		userIds: searchParams.userIds || undefined,
		projectIds: searchParams.projectIds || undefined,
		from: searchParams.from || undefined,
		until: searchParams.until || undefined,
	});

	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};

	const { data, isLoading } = useQuery({
		queryKey: ['company-events', groupId, searchParams],
		queryFn: () => getCompanyEvents(groupId, query),
		enabled: !userLoading,
	});

	const { data: typesResponse } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes('?noPagination=true'),
	});

	const { data: zonesResponse } = useQuery({
		queryKey: ['zones'],
		queryFn: () => getAllZones(),
	});

	const { data: tagsResponse } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: featureFlags?.USES_TAGS,
	});

	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING || false;
	const types = typesResponse?.data || [];
	const zones = zonesResponse?.data || [];
	useBreadcrumbLevel(1, 'Events');

	if (userLoading) {
		return null;
	}

	if (!authenticatedUser) return null;

	const handleSort = ([sort]: any[]) => {
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handleRowClick = ({ row }: any) => {
		updateValue(window.location.href);
		navigate({
			to: '/company/$groupId/events/$eventId',
			params: { groupId, eventId: row.id },
		});
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleFilterChange = (filterData: Record<string, any>) => {
		updateQueryParams(filterData);
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleDateFilterChange = (dateFilter: { from: string; until: string }) => {
		updateQueryParams(dateFilter);
	};

	const handleAddEvent = async (eventData: any) => {
		try {
			const response = await fetch(`/api/company-portal/groups/${groupId}/events`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(eventData),
			});
			if (!response.ok) throw new Error('Failed to create event');
			EventEmitter.emit('toast', { type: 'success', message: 'Event created successfully' });
			queryClient.invalidateQueries({ queryKey: ['company-events', groupId] });
			setDialogOpen(false);
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to create event' });
		}
	};

	const defaultEventData = {
		startTime: startOfDay(new Date()),
		finishTime: endOfDay(new Date()),
		adminIds: [authenticatedUser.id],
		groupIds: [groupId],
	};

	const activeTab = parseInt(searchParams.tab || '0', 10);
	let userIds = searchParams.userIds || [];
	let projectIds = searchParams.projectIds || [];
	return (
		<>
			<div>
				<div className="mt-2 flex flex-row flex-wrap gap-2">
					<div key="search" className="w-3/5 flex-grow flex gap-2">
						<FilterSearch
							label="Search Event"
							defaultValue={searchParams.search || ''}
							onChange={debounce((searchInput: string) => {
								handleSearchChange(searchInput);
							}, 350)}
							sx={{ width: '100%', minWidth: '40%', flexGrow: 1 }}
						/>
						<FilterAsyncAutoComplete
							// dirty fix to reset the value when the user clears the filter
							key={userIds.length > 0 ? 'userFilter' : 'userFilterNoValue'}
							label="Filter Users"
							value={userIds}
							onChange={(userIds: any) => handleFilterChange({ userIds })}
							dataSource="/api/data-sources/users/data"
							optionLabel="name"
							name="userIds"
							multiple
						/>
					</div>
					<div key="filters" className="w-2/5 flex-grow flex gap-2">
						{usesSubEvents && (
							<FilterAsyncAutoComplete
								// dirty fix to reset the value when the user clears the filter
								key={projectIds.length > 0 ? 'projectFilter' : 'projectFilterNoValue'}
								name="projectIds"
								label="Filter Projects"
								filter="groupId"
								values={{ groupId }}
								dataSource="/api/data-sources/projects/data"
								value={projectIds}
								onChange={(projectIds: any) => handleFilterChange({ projectIds })}
								multiple
							/>
						)}
						<FilterAutoCompleteById
							key="typeFilter"
							label="Filter Types"
							options={types}
							value={searchParams.typeIds || []}
							onChange={(typeIds: any) => handleFilterChange({ typeIds })}
							multiple
						/>
						<FilterAutoCompleteById
							key="zoneFilter"
							label="Filter Zones"
							options={zones}
							value={searchParams.zoneIds || []}
							onChange={(zoneIds: any) => handleFilterChange({ zoneIds })}
							multiple
						/>
						{activeTab === 0 && (
							<FilterDateRange
								label="Filter Date Range"
								value={
									searchParams.from || searchParams.until
										? { from: searchParams.from, until: searchParams.until }
										: undefined
								}
								onChange={handleDateFilterChange}
								sx={{ width: 330 }}
							/>
						)}
					</div>
				</div>
				<Paper variant="outlined" className="mt-2">
					<DataGrid
						data={data?.data || []}
						columns={getColumns(usesSubEvents)}
						rowCount={data?.count || 0}
						isLoading={isLoading}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(searchParams.sort)]}
						pageSize={data?.pageSize || 0}
						pageSizeOptions={[data?.pageSize || 0]}
						page={data?.page || 0}
						onRowClick={handleRowClick}
						pagination
						getRowClassName={(p: any) => p.row.markedForReview && 'error'}
						onReload={() => queryClient.invalidateQueries({ queryKey: ['company-events', groupId] })}
					/>
				</Paper>
			</div>

			<EventForm
				data={defaultEventData}
				onClose={() => setDialogOpen(false)}
				isOpen={isDialogOpen}
				onSubmit={handleAddEvent}
				types={types}
				isCompanyPortalEvent={true}
				usesSubEvents={usesSubEvents}
				usesTags={featureFlags?.USES_TAGS || false}
				tags={tagsResponse || []}
				projectGetUrl={`/api/company-portal/groups/${groupId}/projects/`}
			/>
		</>
	);
}
